import { useState, useRef } from "react";
import axios from "axios";
import {
  useGridApiContext,
  gridDataRowIdsSelector,
} from "@mui/x-data-grid-premium";
import {
  Box,
  Button,
  TextField,
  Select,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Toolbar,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  LinearProgress,
  Typography,
  ButtonGroup,
  Menu,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  FileDownload as FileDownloadIcon,
  ViewComfy as ViewComfyIcon,
  ViewCompact as ViewCompactIcon,
  TableRows as TableRowsIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ContentCopy as ContentCopyIcon,
  NoteAdd as NoteAddIcon,
  FilterAltOff as FilterAltOffIcon,
  SortByAlpha as SortByAlphaIcon,
} from "@mui/icons-material";
import { GridResponse } from "./types";

interface CustomToolbarProps {
  onAddClick: () => void;
  onDuplicateClick?: () => void;
  onRefreshClick: () => void;
  enableCreate: boolean;
  enableDuplicate?: boolean;
  selectedRowCount?: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  enableSearch: boolean;
  onFilterClick: () => void;
  onExportClick: () => void;
  getAllRows?: () => Promise<any[]>; // Function to fetch all rows for "ENTIRE TABLE" export
  density: "compact" | "standard" | "comfortable";
  onDensityChange: (density: "compact" | "standard" | "comfortable") => void;
  apiUrl: string;
  globalSearchValue: string;
}

export function CustomToolbar({
  onAddClick,
  onDuplicateClick,
  onRefreshClick,
  enableCreate,
  enableDuplicate = false,
  selectedRowCount = 0,
  searchValue,
  onSearchChange,
  enableSearch,
  onFilterClick,
  onExportClick,
  apiUrl,
  globalSearchValue,
  density,
  onDensityChange,
}: CustomToolbarProps) {
  const apiRef = useGridApiContext();
  const addButtonRef = useRef<HTMLDivElement>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  const totalRowCount = apiRef.current.state.pagination.rowCount;

  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState<
    "PAGE/SELECTED" | "ENTIRE TABLE"
  >("PAGE/SELECTED");

  // Add progress tracking states
  const [exportProgress, setExportProgress] = useState(0);
  const [exportedRows, setExportedRows] = useState(0);

  // Clear filters and sorts confirmation dialogs
  const [clearFiltersDialogOpen, setClearFiltersDialogOpen] = useState(false);
  const [clearSortsDialogOpen, setClearSortsDialogOpen] = useState(false);

  const handleExportTypeChange = (event: SelectChangeEvent) => {
    setExportType(event.target.value as "PAGE/SELECTED" | "ENTIRE TABLE");
  };

  const handleClearFilters = () => {
    apiRef.current.setFilterModel({ items: [] });
    setClearFiltersDialogOpen(false);
  };

  const handleClearSorts = () => {
    apiRef.current.setSortModel([]);
    setClearSortsDialogOpen(false);
  };

  async function exportAllRows() {
    apiRef.current.setLoading(true);
    setExporting(true);
    setExportProgress(0);
    setExportedRows(0);

    const currentRowIds = gridDataRowIdsSelector(apiRef);

    try {
      const filterModel = apiRef.current.state.filter.filterModel;
      const sortModel = apiRef.current.state.sorting.sortModel;

      const CHUNK_SIZE = 1000;
      const allRows: any[] = [];
      const totalPages = Math.ceil(totalRowCount / CHUNK_SIZE);

      // Fetch data sequentially, page by page
      for (let page = 0; page < totalPages; page++) {
        const requestBody = {
          operation: "READ",
          paginationModel: {
            page: page,
            pageSize: CHUNK_SIZE,
          },
          sortModel: sortModel,
          filterModel: filterModel,
          globalSearch: globalSearchValue,
          groupKeys: [],
          groupFields: [],
        };

        const response = await axios.post<GridResponse>(apiUrl, requestBody, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        const fetchedRows = response.data.data;
        allRows.push(...fetchedRows);

        // Update progress
        const progress = Math.min(
          Math.round((allRows.length / totalRowCount) * 100),
          100
        );
        setExportProgress(progress);
        setExportedRows(allRows.length);

        // Break early if we got fewer rows than expected
        if (fetchedRows.length < CHUNK_SIZE) {
          break;
        }

        // Optional: Add a small delay between requests
        if (page < totalPages - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      // Temporarily update grid with all rows
      apiRef.current.updateRows(allRows);

      // Perform the export
      apiRef.current.exportDataAsCsv({
        fileName: `full-export`,
        includeHeaders: true,
      });

      // Find rows that were temporarily added
      const idsToDelete = allRows
        .map((row) => row.id)
        .filter((id) => !currentRowIds.includes(id));

      // Restore to original rows by deleting temporary ones
      apiRef.current.updateRows(
        idsToDelete.map((id) => ({ id, _action: "delete" }))
      );
    } catch (error) {
      console.error("Error exporting all rows:", error);
    } finally {
      setExporting(false);
      setExportProgress(0);
      setExportedRows(0);
      apiRef.current.setLoading(false);
    }
  }

  const handleExport = () => {
    if (exportType === "ENTIRE TABLE") {
      exportAllRows();
    } else {
      onExportClick();
    }
  };

  return (
    <>
      <Toolbar>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}
        >
          {enableSearch && (
            <TextField
              size="small"
              placeholder="Search..."
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              sx={{ minWidth: 300 }}
            />
          )}
          {enableCreate && !enableDuplicate && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={onAddClick}
              variant="outlined"
            >
              Add Record
            </Button>
          )}
          {enableCreate && enableDuplicate && (
            <>
              <ButtonGroup variant="outlined" size="small" ref={addButtonRef}>
                <Button startIcon={<AddIcon />} onClick={onAddClick}>
                  Add Record
                </Button>
                <Button
                  size="small"
                  onClick={() => setAddMenuOpen(true)}
                  sx={{ px: 1 }}
                >
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>
              <Menu
                anchorEl={addButtonRef.current}
                open={addMenuOpen}
                onClose={() => setAddMenuOpen(false)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  onClick={() => {
                    setAddMenuOpen(false);
                    onAddClick();
                  }}
                >
                  <ListItemIcon>
                    <NoteAddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Create New</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setAddMenuOpen(false);
                    if (onDuplicateClick) {
                      onDuplicateClick();
                    }
                  }}
                  disabled={selectedRowCount !== 1}
                >
                  <ListItemIcon>
                    <ContentCopyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    Duplicate Existing
                    {selectedRowCount !== 1 && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        (Select 1 row)
                      </Typography>
                    )}
                  </ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
          <Tooltip title="Refresh Table">
            <IconButton size="small" onClick={onRefreshClick}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filter">
            <IconButton size="small" onClick={onFilterClick}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          <Button
            size="small"
            startIcon={<FilterAltOffIcon />}
            onClick={() => setClearFiltersDialogOpen(true)}
            variant="outlined"
          >
            CLEAR FILTERS
          </Button>

          <Button
            size="small"
            startIcon={<SortByAlphaIcon />}
            onClick={() => setClearSortsDialogOpen(true)}
            variant="outlined"
          >
            CLEAR SORTS
          </Button>

          {/* Export Button with Type Selector */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Export">
              <span>
                <IconButton
                  size="small"
                  onClick={handleExport}
                  disabled={exporting}
                >
                  <FileDownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Select
              title="Export type"
              value={exportType}
              onChange={handleExportTypeChange}
              size="small"
              sx={{
                minWidth: 160,
                "& .MuiSelect-select": {
                  py: 0.5,
                  fontSize: "0.875rem",
                },
              }}
              disabled={exporting}
            >
              <MenuItem
                value="PAGE/SELECTED"
                title="Export all selected rows or all rows on the current page"
              >
                PAGE/SELECTED
              </MenuItem>
              <MenuItem
                value="ENTIRE TABLE"
                title="Export all rows from this table (with any active table filters applied)"
              >
                ENTIRE TABLE
              </MenuItem>
            </Select>
          </Box>

          <Tooltip title="Density">
            <ToggleButtonGroup
              value={density}
              exclusive
              onChange={(_, newDensity) => {
                if (newDensity !== null) {
                  onDensityChange(newDensity);
                }
              }}
              size="small"
            >
              <ToggleButton value="compact">
                <ViewCompactIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="standard">
                <ViewComfyIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="comfortable">
                <TableRowsIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
        </Box>
      </Toolbar>

      {/* Export Progress Modal */}
      <Dialog open={exporting} disableEscapeKeyDown maxWidth="sm" fullWidth>
        <DialogTitle>Exporting Data</DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%", mt: 2, mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Fetching rows: {exportedRows.toLocaleString()} of{" "}
              {totalRowCount.toLocaleString()}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={exportProgress}
              sx={{ height: 8, borderRadius: 4, mt: 1 }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 1 }}
            >
              {exportProgress}%
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Please wait while we prepare your export...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Clear Filters Confirmation Dialog */}
      <Dialog
        open={clearFiltersDialogOpen}
        onClose={() => setClearFiltersDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Clear All Filters?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to clear all active filters? This action will
            remove all filtering criteria from the table.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setClearFiltersDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button onClick={handleClearFilters} variant="contained" color="error">
            Clear Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Sorts Confirmation Dialog */}
      <Dialog
        open={clearSortsDialogOpen}
        onClose={() => setClearSortsDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Clear All Sorts?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to clear all active sorting? This action will
            remove all sorting criteria from the table.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setClearSortsDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button onClick={handleClearSorts} variant="contained" color="error">
            Clear Sorts
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
